import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { useHasPermission } from "./session-context"
import { SearchIcon } from "./components/icons"
import { SponsorsPageSkeleton } from "./components/skeleton"
import { SponsorMatchModal } from "./components/sponsor-match-modal"
import { StatusTabs } from "./components/status-tabs"
import { ContentPagination } from "./content-components"
import { RowActionsMenu } from "./components/row-actions-menu"

type SponsorStatus = "active" | "paused" | "lapsed"
type SponsorTier = "sustainer" | "founder" | "friend"
type SponsorshipRequestStatus = "new" | "contacted" | "approved" | "declined" | "closed"
const PAGE_SIZE = 10

interface SponsorListItem {
  id: string
  code: string
  name: string
  email: string
  tier: SponsorTier
  childrenCount: number
  lifetimeDonated: number
  lastGiftAt: string | null
  status: SponsorStatus
  sponsorshipRequestStatus: SponsorshipRequestStatus | null
}

interface StatusCounts {
  all: number
  active: number
  paused: number
  lapsed: number
}

const STATUS_COLORS: Record<SponsorStatus | SponsorshipRequestStatus, { bg: string; text: string }> = {
  active: { bg: "bg-green-100", text: "text-green-700" },
  paused: { bg: "bg-gold-100", text: "text-gold-700" },
  lapsed: { bg: "bg-red-100", text: "text-red-700" },
  new: { bg: "bg-gold-100", text: "text-gold-700" },
  contacted: { bg: "bg-blue-100", text: "text-blue-700" },
  approved: { bg: "bg-green-100", text: "text-green-700" },
  declined: { bg: "bg-red-100", text: "text-red-700" },
  closed: { bg: "bg-ink/10", text: "text-ink/70" },
}

const STATUS_LABELS: Record<SponsorStatus | SponsorshipRequestStatus, string> = {
  active: "Active",
  paused: "Paused",
  lapsed: "Lapsed",
  new: "Submitted",
  contacted: "Reviewing",
  approved: "Approved",
  declined: "Declined",
  closed: "Closed",
}

const TIER_LABELS: Record<SponsorTier, string> = {
  sustainer: "Sustainer",
  founder: "Founder",
  friend: "Friend",
}

function formatCurrency(amountInKobo: number): string {
  const naira = amountInKobo / 100
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(naira)
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "—"
  const date = new Date(dateString)
  return date.toLocaleDateString("en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function SponsorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

export function SponsorsPage() {
  const navigate = useNavigate()
  const canView = useHasPermission("sponsors")
  const canMutate = useHasPermission("sponsors_mutate")
  const [sponsors, setSponsors] = useState<SponsorListItem[]>([])
  const [counts, setCounts] = useState<StatusCounts>({ all: 0, active: 0, paused: 0, lapsed: 0 })
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<SponsorStatus | "all">("all")
  const [search, setSearch] = useState("")
  const [matchSponsorId, setMatchSponsorId] = useState<string | null>(null)

  const fetchSponsors = async () => {
    try {
      const query: { status?: SponsorStatus; search?: string; limit: string; offset: string } = {
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      }
      if (activeTab !== "all") query.status = activeTab
      if (search.trim()) query.search = search.trim()

      const res = await client.api.admin.sponsors.$get({ query })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setSponsors(body.data.items as SponsorListItem[])
        setCounts(body.data.counts)
        setTotal(body.data.total)
      }
    } catch {
      setError("Failed to load sponsors")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchSponsors()
    } else {
      setLoading(false)
    }
  }, [activeTab, canView, page])

  useEffect(() => {
    setPage(1)
  }, [activeTab, search])

  // Debounced search
  useEffect(() => {
    if (!canView) return
    const timer = setTimeout(() => {
      void fetchSponsors()
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Sponsors</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view sponsors.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <SponsorsPageSkeleton />
  }

  const tabs: { key: SponsorStatus | "all"; label: string; count: number }[] = [
    { key: "all", label: "All", count: counts.all },
    { key: "active", label: "Active", count: counts.active },
    { key: "paused", label: "Paused", count: counts.paused },
    { key: "lapsed", label: "Lapsed", count: counts.lapsed },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Sponsors</h1>
          <p className="mt-1 text-sm text-ink/60">{counts.all} sponsors supporting the foundation.</p>
        </div>
        {canMutate && (
          <button
            onClick={() => navigate("/admin/sponsors/new")}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-forest-950 px-5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
          >
            <SponsorIcon className="h-4 w-4" />
            Add sponsor
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <StatusTabs
          tabs={tabs}
          activeKey={activeTab}
          onChange={setActiveTab}
          ariaLabel="Sponsor status"
        />

        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 w-64 rounded-full border border-gold-500 bg-cream-50 pl-10 pr-4 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>
      </div>

      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : sponsors.length === 0 ? (
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">No sponsors found</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gold-500/50">
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Sponsor
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Tier
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Children
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Total donated
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Last gift
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Status
                  </th>
                  {canMutate && <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-ink/60">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gold-500/30">
                {sponsors.map((sponsor) => (
                  <tr
                    key={sponsor.id}
                    onClick={() => navigate(`/admin/sponsors/${sponsor.id}`)}
                    className="cursor-pointer hover:bg-cream-50"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-950 text-xs font-bold text-white">
                          {sponsor.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-ink">{sponsor.name}</p>
                          <p className="text-xs text-ink/50">{sponsor.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">{TIER_LABELS[sponsor.tier]}</td>
                    <td className="px-5 py-4 text-sm text-ink">{sponsor.childrenCount}</td>
                    <td className="px-5 py-4 text-sm text-ink">{formatCurrency(sponsor.lifetimeDonated)}</td>
                    <td className="px-5 py-4 text-sm text-ink">{formatDate(sponsor.lastGiftAt)}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                           STATUS_COLORS[sponsor.sponsorshipRequestStatus ?? sponsor.status].bg
                         } ${STATUS_COLORS[sponsor.sponsorshipRequestStatus ?? sponsor.status].text}`}
                       >
                         {STATUS_LABELS[sponsor.sponsorshipRequestStatus ?? sponsor.status]}
                       </span>
                    </td>
                    {canMutate && (
                      <td className="px-5 py-4 text-right">
                        <RowActionsMenu
                          label={`Actions for ${sponsor.name}`}
                          items={[
                            {
                              label: "View record",
                              onClick: () => navigate(`/admin/sponsors/${sponsor.id}`),
                            },
                             ...(sponsor.status === "active" && sponsor.sponsorshipRequestStatus === "approved"
                               ? [{ label: "Match child", onClick: () => setMatchSponsorId(sponsor.id) }]
                              : []),
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ContentPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} disabled={loading} />

      <SponsorMatchModal
        open={matchSponsorId !== null}
        onClose={() => setMatchSponsorId(null)}
        sponsorId={matchSponsorId ?? undefined}
        onMatched={() => {
          setMatchSponsorId(null)
          void fetchSponsors()
        }}
      />
    </div>
  )
}
